import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock } from 'lucide-react';
import { TosLink } from '../components/TosModal';
import API from '../utils/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Reverted to Emergent-managed Google Auth until our own OAuth app passes Google verification.
    // The custom backend route /api/auth/google/start is still wired and ready to switch back.
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F6F7FC] flex items-center justify-center p-6">
      <div className="absolute inset-0 opacity-60 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-[#DDE1EE] to-[#A8AAD4] rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-[#A8AAD4] to-[#DDE1EE] rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="card-surface p-8">
          <div className="text-center mb-8">
            <Link to="/" className="cursor-pointer">
              <h1 className="font-serif-display text-4xl font-bold mb-2 text-[#2D2560]">
              Parentum
            </h1>
            </Link>
            <p className="text-[#6B6690]">Welcome back</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div data-testid="login-email-field">
              <label className="block text-sm text-[#3A3566] font-medium mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8C86C4]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] placeholder:text-[#A8AAD4] focus:ring-2 focus:ring-[#5A4E9E] focus:border-[#5A4E9E] rounded-lg pl-11 pr-4 py-3 outline-none transition-all"
                  placeholder="you@example.com"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div data-testid="login-password-field">
              <label className="block text-sm text-[#3A3566] font-medium mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8C86C4]" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] placeholder:text-[#A8AAD4] focus:ring-2 focus:ring-[#5A4E9E] focus:border-[#5A4E9E] rounded-lg pl-11 pr-4 py-3 outline-none transition-all"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] rounded-lg p-3 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full nebula-gradient cta-glow font-semibold tracking-wide rounded-lg py-3 transition-opacity hover:opacity-95 disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#DDE1EE]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-white text-[#6B6690]">Or continue with</span>
            </div>
          </div>

          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 bg-white border border-[#DDE1EE] text-[#1E1A3E] hover:bg-[#F6F7FC] font-semibold rounded-lg py-3 transition-colors"
            data-testid="google-login-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>

          <div className="mt-6 text-center">
            <Link to="/forgot-password" className="text-sm text-[#6B6690] hover:text-[#2D2560] transition-colors">
              Forgot password?
            </Link>
          </div>

          <p className="text-center text-[#6B6690] text-sm mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-[#2D2560] hover:text-[#5A4E9E] font-semibold transition-colors" data-testid="signup-link">
              Sign up
            </Link>
          </p>

          <div className="text-center mt-4">
            <TosLink className="text-[#8C86C4] hover:text-[#2D2560] text-xs transition-colors" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
